import { ERROR_TYPES, ERROR_LEVELS, ERROR_TYPE_PATTERNS } from '../../constants/errors/errorConstants';

function extractFilenameFromStack(stack) {
  if (!stack) return null;

  const match = stack.match(/(?:at|@)\s*(?:.*\s+\()?([^:)]+):(\d+):(\d+)/);
  return match ? match[1] : null;
}

function extractLineFromStack(stack) {
  if (!stack) return null;

  const match = stack.match(/(?:at|@)\s*(?:.*\s+\()?([^:)]+):(\d+):(\d+)/);
  return match ? match[2] : null;
}


function classifyError(error) {
  if (isCriticalGlobalError(error)) {
    return ERROR_LEVELS.CRITICAL;
  }
}

function isRrwebStackOverflow(error) {
  const errorMessage = error?.message || error?.toString() || '';
  const errorStack = error?.stack || '';
  const errorName = error?.name || '';

  // Vérifier si c'est un stack overflow (plusieurs façons)
  const isStackOverflow =
    /Maximum call stack size exceeded/i.test(errorMessage) ||
    /Maximum call stack size exceeded/i.test(errorStack) ||
    (errorName === 'RangeError' && /stack/i.test(errorMessage));

  // Vérifier si c'est causé par rrweb (stack corrompue = signature rrweb)
  const hasCorruptedStack =
    /(@←[^@]*){5,}/.test(errorStack) ||
    /(@[a-z]@[a-z]){5,}/i.test(errorStack) ||
    (errorStack.match(/@/g)?.length || 0) > 50;

  const isTranslationActive =
    document.documentElement.classList.contains('translated-ltr') ||
    document.documentElement.classList.contains('translated-rtl') ||
    !!document.querySelector('font[dir="auto"]');

  const rrwebPatterns = [/rrweb/i, /posthog/i, /snapshot/i];
  const isRrwebInStack = rrwebPatterns.some(pattern => pattern.test(errorStack));

  // DEBUG
  console.log('=== isRrwebStackOverflow ===');
  console.log('errorMessage:', errorMessage);
  console.log('errorName:', errorName);
  console.log('isStackOverflow:', isStackOverflow);
  console.log('hasCorruptedStack:', hasCorruptedStack);
  // if (!isStackOverflow) {
  //   console.warn('StackOverflow detected - likely rrweb issue');
  //   return false;
  // }

  // C'est rrweb si : stack corrompue OU (stack overflow + traduction/rrweb)
  if (hasCorruptedStack) {
    console.warn('Corrupted stack detected - likely rrweb issue');
    return true;
  }

  if (isStackOverflow && (isTranslationActive || isRrwebInStack)) {
    console.warn('Stack overflow with translation/rrweb - ignoring');
    return true;
  }

  return false;
}

function isCriticalGlobalError(error) {
  const errorMessage = error?.message || error?.toString() || '';
  const errorStack = error?.stack || '';

  if (isRrwebStackOverflow(error)) {
    console.warn('Ignored rrweb stack overflow (browser translation)');
    return false;
  }

  const exclusionPatterns = [
    /valid_domains.*is not a function/i,
    /webkit-masked-url/i,
    /macadam/i,
    /darkreader/i,
    /getBoundingClientRect.*not.*function/i,
  ];

  if (exclusionPatterns.some((pattern) =>
    pattern.test(errorMessage) || pattern.test(errorStack)
  )) {
    return false;
  }
  const criticalPatterns = [
    // Memory errors
    /Maximum call stack size exceeded/i,
    /out of memory/i,
    /RangeError.*Maximum call stack/i,
    /Cannot allocate memory/i,
    /Stack overflow/i,

    // Loading errors
    /Script error/i,
    /ChunkLoadError/i,
    /Loading chunk \d+ failed/i,
    /Loading CSS chunk \d+ failed/i,
    /Failed to import dynamically/i,

    // JavaScript errors
    /.*is not a function/i,
    /.*is not defined/i,
    /ReferenceError/i,
    /TypeError.*undefined/i,
    /TypeError.*null/i,
    /Cannot read propert.*of (null|undefined)/i,
    /Cannot set propert.*of (null|undefined)/i,
    /Cannot access before initialization/i,

    // Syntax errors
    /SyntaxError/i,
    /Unexpected token/i,
    /Unexpected end of input/i,
    /Invalid regular expression/i,

    // React errors
    /React.*render.*error/i,
    /Cannot update.*unmounted component/i,
    /Invariant Violation/i,
    /Minified React error/i,
    /React.*Hook.*called/i,

    // Security errors
    /Content Security Policy/i,
    /Mixed Content/i,
    /Blocked.*mixed content/i,

    // Bundle/compilation errors
    /Module not found/i,
    /Can't resolve/i,
    /Failed to compile/i,
    /Build failed/i,

    // System errors
    /SecurityError/i,
    /DOMException.*security/i,
    /Access.*denied/i,
    /Iframe.*access.*denied/i
  ];

  return (
    criticalPatterns.some((pattern) => pattern.test(errorMessage))
  );
}


function serializeError(error, event = null) {
  const errorObj = error || event?.error || event?.reason || {};

  const errorName = errorObj?.name || errorObj?.constructor?.name || 'UnknownError';
  const errorMessage = errorObj?.message || errorObj?.toString?.() || 'No error message available';
  const errorStack = errorObj?.stack || 'No stack trace available';
  const apiUrl = error?.request?.responseURL ||  null;
  const responseMessage = error?.response?.data?.message || null;

  const filename = event?.filename || errorObj?.filename || extractFilenameFromStack(errorStack);
  const lineno = event?.lineno || errorObj?.lineno || extractLineFromStack(errorStack);
  const colno = event?.colno || errorObj?.colno || null;

  const errorType = determineErrorType(errorName, errorMessage, errorStack);
  const errorCategory = classifyErrorByType(errorObj);

  return {
    errorName,
    errorMessage,
    errorType,
    errorCategory,
    apiUrl,
    responseMessage,
    responseStatus: error?.status || null,
    stackTrace: errorStack,
    filename,
    lineno,
    colno,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    constructor: errorObj?.constructor?.name,
    originalError: {
      name: errorObj?.name,
      message: errorObj?.message,
      toString: errorObj?.toString?.()
    }
  };
}

function determineErrorType(errorName, errorMessage, stack) {
  if (!errorName || errorName === 'Error') {
    if (/network|fetch|xhr|cors/i.test(errorMessage)) return 'NetworkError';
    if (/chunk|loading|script/i.test(errorMessage)) return 'ResourceError';
    if (/not defined|is not a function/i.test(errorMessage)) return 'ReferenceError';
    return 'UnknownError';
  }
  return errorName;
}

function classifyErrorByType(error) {
  const errorMessage = error?.message || error?.toString() || '';

  for (const [errorType, patterns] of Object.entries(ERROR_TYPE_PATTERNS)) {
    const matchedPattern = patterns.find(patternObj => patternObj.pattern.test(errorMessage));
    if (matchedPattern) {
      return { errorType, criticality: matchedPattern.criticality };
    }
  }

  return { errorType: ERROR_TYPES.UNKNOWN, criticality: ERROR_LEVELS.UNKNOWN };
}

function isNetworkError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.NETWORK;
}

function isRuntimeError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.RUNTIME;
}

function isValidationError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.VALIDATION;
}

function isResourceError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.RESOURCE;
}

function isStorageError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.STORAGE;
}

function isMediaError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.MEDIA;
}

function isBrowserError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.BROWSER;
}

function isPerformanceError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.PERFORMANCE;
}

function isConfigurationError(error) {
  return classifyErrorByType(error).errorType === ERROR_TYPES.CONFIGURATION;
}

function getErrorTypeInfo(errorType) {
  const errorTypeInfo = {
    [ERROR_TYPES.NETWORK]: {
      name: 'Network & API Error',
      description: 'Erreurs réseau, API et connectivité',
      severity: 'high',
      category: 'connectivity',
      icon: '🌐'
    },
    [ERROR_TYPES.RUNTIME]: {
      name: 'Runtime Error',
      description: 'Erreurs JavaScript, React, mémoire et syntaxe',
      severity: 'critical',
      category: 'code',
      icon: '⚠️'
    },
    [ERROR_TYPES.VALIDATION]: {
      name: 'Validation & Auth Error',
      description: 'Erreurs de validation et authentification',
      severity: 'medium',
      category: 'user_input',
      icon: '🔒'
    },
    [ERROR_TYPES.RESOURCE]: {
      name: 'Resource & Bundle Error',
      description: 'Erreurs de chargement de ressources et bundling',
      severity: 'high',
      category: 'assets',
      icon: '📦'
    },
    [ERROR_TYPES.STORAGE]: {
      name: 'Storage & Database Error',
      description: 'Erreurs de stockage et base de données',
      severity: 'medium',
      category: 'storage',
      icon: '💾'
    },
    [ERROR_TYPES.MEDIA]: {
      name: 'Media & Upload Error',
      description: 'Erreurs média, géolocalisation et upload',
      severity: 'medium',
      category: 'media',
      icon: '📸'
    },
    [ERROR_TYPES.BROWSER]: {
      name: 'Browser Compatibility Error',
      description: 'Erreurs de compatibilité et features non supportées',
      severity: 'low',
      category: 'compatibility',
      icon: '🌍'
    },
    [ERROR_TYPES.PERFORMANCE]: {
      name: 'Performance Error',
      description: 'Erreurs de performance et WebSocket',
      severity: 'medium',
      category: 'performance',
      icon: '⚡'
    },
    [ERROR_TYPES.CONFIGURATION]: {
      name: 'Configuration Error',
      description: 'Erreurs de config, analytics, thèmes et développement',
      severity: 'low',
      category: 'configuration',
      icon: '⚙️'
    }
  };

  return errorTypeInfo[errorType] || {
    name: 'Unknown Error',
    description: 'Type d\'erreur non identifié',
    severity: 'unknown',
    category: 'unknown',
    icon: '❓'
  };
}

function serializeCrashData(crashData, event = null) {
  let error = null;
  let eventId = null;

  if (crashData && typeof crashData === 'object') {
    if (crashData.error) {
      error = crashData.error;
      eventId = crashData.eventId;
    }
    else if (crashData.message || crashData.stack || crashData.name) {
      error = crashData;
    }
    else {
      error = crashData;
    }
  } else {
    error = crashData;
  }

  let errorName = 'UnknownError';
  let errorMessage = 'No error message available';
  let errorStack = 'No stack trace available';

  if (error) {
    if (error.name) {
      errorName = error.name;
    } else if (error.constructor && error.constructor.name && error.constructor.name !== 'Object') {
      errorName = error.constructor.name;
    } else if (typeof error === 'string') {
      errorName = 'StringError';
      errorMessage = error;
    } else if (error.message && error.message.includes('ChunkLoadError')) {
      errorName = 'ChunkLoadError';
    }

    if (error.message) {
      errorMessage = error.message;
    } else if (error.toString && typeof error.toString === 'function' && error.toString() !== '[object Object]') {
      errorMessage = error.toString();
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error.stack) {
      const firstLine = error.stack.split('\n')[0];
      if (firstLine && firstLine.includes(':')) {
        errorMessage = firstLine.split(':').slice(1).join(':').trim();
      }
    }

    if (error.stack) {
      errorStack = error.stack;
    }
  }

  if (errorMessage === '[object Object]' && errorStack.includes('ChunkLoadError')) {
    errorName = 'ChunkLoadError';
    errorMessage = 'ChunkLoadError - Failed to load application chunk';
  }

  const filename = event?.filename || extractFilenameFromStack(errorStack);
  const lineno = event?.lineno || extractLineFromStack(errorStack);
  const colno = event?.colno || null;

  const errorType = determineErrorType(errorName, errorMessage, errorStack);
  const errorCategory = classifyErrorByType({ message: errorMessage, name: errorName, stack: errorStack });

  const result = {
    errorName,
    errorMessage,
    errorType,
    errorCategory,
    stackTrace: errorStack,
    filename,
    url: window.location.href,
  };


  return result;
}

export {
  classifyError,
  serializeError,
  serializeCrashData,
  classifyErrorByType,
  isNetworkError,
  isRuntimeError,
  isValidationError,
  isResourceError,
  isStorageError,
  isMediaError,
  isBrowserError,
  isPerformanceError,
  isConfigurationError,
  getErrorTypeInfo,
  ERROR_TYPES,
  ERROR_TYPE_PATTERNS,
  ERROR_LEVELS,
};
